import React, { useState } from 'react'
import dayjs from 'dayjs'
import {
  Checkbox,
  createStyles,
  makeStyles,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
  withStyles,
  Grid,
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import clsx from 'clsx'
import { isPurchasedItem, truncateString } from '../../utils/utils'
import Price from '../Shop/Price'
import CMSText from '../Global/CMSText'

const useStyles = makeStyles((theme) =>
  createStyles({
    productCard: {
      minHeight: 70,
      [theme.breakpoints.down('md')]: {
        font: '12px',
      },
    },
    productCardConfirmationPage: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        justifyContent: 'space-between',
        width: '100%',
      },
    },
    checkBox: {},
    imgContainer: {
      height: 60,
    },
    imgContainerConfirmationPage: {
      width: 85,
      height: 85,
      position: 'relative',
      marginRight: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        marginRight: theme.spacing(2),
        width: 50,
        height: 50,
      },
    },
    imgProduct: {
      height: '100%',
      objectFit: 'contain',
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(0),
      },
    },
    imgProductConfirmationPage: {
      width: '100%',
      objectFit: 'contain',
      marginLeft: theme.spacing(0),
      [theme.breakpoints.up('md')]: {},
    },
    productInformationConfirmationPage: {
      width: '-webkit-fill-available',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
    },
    productName: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing(2),
        width: '80%',
        padding: theme.spacing(2),
      },
    },
    productNameConfirmationPage: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginLeft: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        width: '80%',
      },
    },
    productNameString: {
      paddingRight: theme.spacing(1),
      [theme.breakpoints.down('md')]: {
        fontSize: 11,
      },
    },
    productNameStringConfirmationPage: {
      paddingRight: theme.spacing(2),
    },
    productNotEligibleToReturn: {
      fontSize: 13,
      [theme.breakpoints.down('md')]: {
        fontSize: 11,
      },
    },
    informationIcon: {
      fontSize: 13,
      marginLeft: theme.spacing(0.5),
    },
    productQuantity: {
      width: '10%',
    },
    productQuantityConfirmationPage: {
      width: '15%',
    },
    smallerText: {
      fontSize: 13,
      [theme.breakpoints.down('md')]: {
        fontSize: 10,
      },
      whiteSpace: 'nowrap',
    },
    priceMonth: {
      width: '30%',
    },
    priceMonthConfirmationPage: {
      paddingLeft: theme.spacing(2),
    },
  })
)

type Tprops = {
  isModifying?: boolean
  isSelected?: boolean
  isReturned?: boolean
  showSizes?: boolean
  puid?: string
  t: any
  handleProductSelection?: (
    sku: string,
    total: number,
    index: number,
    quantity: number,
    orderToken: string,
    item: object,
    orderNumber: string,
    warehouseId: string,
    carrierId: number
  ) => any
  settings: any
  item: any
  orderNumber?: string
  index: number
  unit?: any
  warehouseId?: any
  isConfirmation?: boolean
  orderToken?: any
  carrierId?: number
  isBought?: boolean
  showQuantity?: boolean
  showPrice?: boolean
  doc?: any
}

const MainTooltip = withStyles((theme) => ({
  tooltip: {
    textAlign: 'center',
    padding: theme.spacing(1),
    lineHeight: 1,
    '&:hover, &:focus': {
      cursor: 'default',
    },
  },
}))(Tooltip)

const OrderProductRow = ({
  isModifying,
  isSelected,
  showSizes,
  handleProductSelection,
  item,
  t,
  settings,
  puid,
  isReturned = false,
  isConfirmation,
  isBought = false,
  index,
  showQuantity = true,
  orderNumber,
  warehouseId,
  unit,
  carrierId,
  orderToken,
  showPrice,
  doc,
}: Tprops) => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const displayFlexPrice = settings?.display_flex_price_a_day
  const isDisabled =
    item?.units?.length > 0
      ? dayjs().isBefore(item?.units[0]?.startLeasingDate)
      : true
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <>
      {!isConfirmation ? (
        <Grid
          container
          direction="row"
          alignItems="center"
          className={classes.productCard}
          spacing={2}
        >
          <Grid sm={1} xs={1} className={classes.checkBox} alignItems="center">
            {isModifying && !isReturned && (
              <Slide direction="right" in mountOnEnter unmountOnExit>
                <Checkbox
                  checked={isSelected}
                  onChange={() =>
                    handleProductSelection(
                      item.variant.sku,
                      displayFlexPrice
                        ? item.unitPrice
                        : item.total / item?.quantity,
                      index,
                      1,
                      orderToken ? orderToken : item?.token,
                      item,
                      orderNumber,
                      warehouseId,
                      carrierId
                    )
                  }
                  name={item.variant.product.name}
                  data-testid={`order__checkbox--${index}`}
                  disabled={isDisabled}
                />
              </Slide>
            )}
          </Grid>
          <Grid
            sm={1}
            xs={2}
            style={{ justifyContent: 'center', display: 'flex' }}
            alignItems="center"
          >
            <img
              src={item.variant.product?.images[0]?.thumb}
              alt={item.variant.product.name}
              width={40}
              className={classes.imgProduct}
            />
          </Grid>
          <Grid
            sm={10}
            xs={9}
            style={{ justifyContent: 'left', display: 'flex' }}
            alignItems="center"
          >
            {
              <Grid sm={1} className={classes.productQuantity}>
                <Typography variant={'body2'}>x {item.quantity}</Typography>
              </Grid>
            }
            {isSmallScreen ? (
              <Grid xs={6} alignItems="center">
                <Typography
                  variant={'h5'}
                  className={classes.productNameString}
                >
                  {truncateString(item.variant.product.name, 24)}
                </Typography>
              </Grid>
            ) : (
              <Grid sm={6} alignItems="center">
                <Typography variant={'h5'}>
                  {item.variant.product.name}
                  {showQuantity && (
                    <Typography variant={'caption'}>
                      &nbsp;x&nbsp;{item?.quantity}
                    </Typography>
                  )}
                </Typography>
              </Grid>
            )}
            {showSizes && (
              <Grid sm={3} xs={3} alignItems="center">
                <Typography variant={'body2'}>
                  {t('texts:subscription_order:size')}{' '}
                  {item?.variant?.newSize?.name}
                </Typography>
              </Grid>
            )}
            {puid && (
              <Grid sm={2} xs={2} alignItems="center">
                <Typography variant={'body2'}>
                  <small>Code:&nbsp;{puid}</small>
                </Typography>
              </Grid>
            )}
            {unit && (
              <Grid sm={2} alignItems="center">
                <Typography variant={'caption'}>
                  {t('texts:subscription_order:returned_date')}&nbsp;
                  {dayjs(unit?.endLeasingDate).format('L')}
                </Typography>
              </Grid>
            )}
            <Grid sm={2} xs={2}>
              {!displayFlexPrice &&
                showPrice &&
                process.env.STORE_TYPE !== 'classic_subscription' && (
                  <Typography variant={'body2'} className={classes.smallerText}>
                    {isReturned || isBought ? (
                      <Price
                        amount={item.total / item.quantity}
                        showDayOrMonth={false}
                        buyAble={isPurchasedItem(item)}
                        dayOrMonth={settings?.day_or_month}
                        isShort={false}
                        displayFlexPrice={false}
                        showUnitText={item.quantity > 1}
                        UnitText={settings?.config_unit_text}
                      />
                    ) : (
                      <Price
                        amount={item.unitPrice}
                        showDayOrMonth
                        buyAble={isPurchasedItem(item)}
                        dayOrMonth={settings?.day_or_month}
                        isShort={false}
                        displayFlexPrice={displayFlexPrice}
                        showUnitText={item.quantity > 1}
                        UnitText={settings?.config_unit_text}
                      />
                    )}
                  </Typography>
                )}
            </Grid>
            {isModifying && !isReturned && isDisabled && (
              <Grid
                sm={4}
                xs={1}
                className={classes.productNotEligibleToReturn}
              >
                {!isSmallScreen && (
                  <CMSText
                    asText
                    data={doc?.data?.product_not_eligible_for_return}
                    defaultText={t(
                      'texts:subscription_order:product_not_eligible_for_return'
                    )}
                  />
                )}
                <MainTooltip
                  title={
                    <CMSText
                      asText
                      data={doc?.data?.tooltip_product_not_eligible_for_return}
                      defaultText={t(
                        'texts:subscription_order:tooltip_product_not_eligible_for_return'
                      )}
                    />
                  }
                  placement="bottom"
                  open={showTooltip}
                  enterDelay={2000}
                >
                  <span
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <InfoIcon className={classes.informationIcon} />
                  </span>
                </MainTooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
      ) : (
        <div
          className={clsx(
            classes.productCard,
            isConfirmation && classes.productCardConfirmationPage
          )}
        >
          <div
            className={
              isConfirmation
                ? classes.imgContainerConfirmationPage
                : classes.imgContainer
            }
          >
            <img
              src={item?.product?.images[0]?.cachedPath}
              alt={item?.product?.name}
              className={
                isConfirmation
                  ? classes.imgProductConfirmationPage
                  : classes.imgProduct
              }
            />
          </div>
          <div
            className={
              isConfirmation && classes.productInformationConfirmationPage
            }
          >
            <div
              className={
                isConfirmation
                  ? classes.productNameConfirmationPage
                  : classes.productName
              }
            >
              {
                <div
                  className={
                    isConfirmation && isSmallScreen
                      ? classes.productQuantityConfirmationPage
                      : classes.productQuantity
                  }
                >
                  <Typography variant={'body2'}>x {item.quantity}</Typography>
                </div>
              }
              {isSmallScreen ? (
                <Typography
                  variant={'h5'}
                  className={
                    isConfirmation && isSmallScreen
                      ? classes.productNameStringConfirmationPage
                      : classes.productNameString
                  }
                >
                  {truncateString(item.product.name, 24)}
                </Typography>
              ) : (
                <Typography variant={'h5'}>{item.product.name}</Typography>
              )}
              {showSizes && !item.product.isSimpleProduct && (
                <Typography variant={'body2'}>
                  {t('texts:subscription_order:size')}&nbsp;
                  {item?.product?.variants[0]?.size}
                </Typography>
              )}
              {puid && (
                <Typography variant={'body2'}>
                  <small>Code:&nbsp;{puid}</small>
                </Typography>
              )}
              {unit && (
                <Typography variant={'caption'}>
                  {t('texts:subscription_order:returned_date')}&nbsp;
                  {unit.endLeasingDate}
                </Typography>
              )}
            </div>
            <div
              className={
                isConfirmation
                  ? classes.priceMonthConfirmationPage
                  : classes.priceMonth
              }
            >
              {showPrice && process.env.STORE_TYPE !== 'classic_subscription' && (
                <Typography variant={'body2'} className={classes.smallerText}>
                  {item?.totalWithoutPromotion && !isConfirmation ? (
                    <Price
                      amount={
                        isPurchasedItem(item)
                          ? item.unitPrice
                          : item.totalWithoutPromotion
                      }
                      showDayOrMonth
                      dayOrMonth={settings?.day_or_month}
                      buyAble={isPurchasedItem(item)}
                      isShort={false}
                      displayFlexPrice={displayFlexPrice}
                      showUnitText={item.quantity > 1}
                      UnitText={settings?.config_unit_text}
                    />
                  ) : (
                    <Price
                      amount={
                        item?.product?.variants[0].price?.current
                          ? item?.product?.variants[0].price?.current *
                            item.quantity
                          : item.total
                      }
                      showDayOrMonth
                      buyAble={isPurchasedItem(item)}
                      dayOrMonth={settings?.day_or_month}
                      isShort={false}
                      displayFlexPrice={displayFlexPrice}
                      showUnitText={item.quantity > 1}
                      UnitText={settings?.config_unit_text}
                    />
                  )}
                </Typography>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default OrderProductRow
